import { Outlet, NavLink } from 'react-router-dom';
import { Truck, Warehouse, ClipboardCheck, FileText, LayoutDashboard, LogOut, Database, Activity, Workflow, Settings, Shield, List, ChevronDown, ChevronRight, BarChart3, Clock, Monitor, Package, TrendingUp } from 'lucide-react';
import { authService } from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const Layout = () => {
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({
    operations: false,
    gateManagement: false,
    dockManagement: false,
    qualityGRN: false,
    workflow: false,
    system: false
  });

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const getMenuKey = (label) => {
    // Convert label to camelCase for menu key
    return label.replace(/\s+/g, '').replace(/^./, str => str.toLowerCase());
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },

    // Workflow submenu - CRITICAL, moved to top
    {
      label: 'Workflow',
      icon: Workflow,
      submenu: [
        { path: '/workflow-engine', label: 'Engine', icon: Workflow },
        { path: '/workflow-config', label: 'Configuration', icon: Settings },
      ]
    },

    // Operations submenu
    {
      label: 'Operations',
      icon: Activity,
      submenu: [
        { path: '/operational-dashboard', label: 'Live Dashboard', icon: Activity },
        { path: '/andon-board', label: 'Andon Board', icon: Monitor },
        { path: '/operational-analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/analytics-advanced', label: 'Advanced Analytics & Alerts', icon: TrendingUp },
      ]
    },

    // Gate Management submenu
    {
      label: 'Gate Management',
      icon: Truck,
      submenu: [
        { path: '/gate-entry', label: 'Register Truck', icon: Truck },
        { path: '/gate-entry-list', label: 'Entry List', icon: List },
      ]
    },

    // Dock Management submenu
    {
      label: 'Dock Management',
      icon: Warehouse,
      submenu: [
        { path: '/dock-management', label: 'Manage Docks', icon: Warehouse },
        { path: '/truck-queue', label: 'Truck Queue', icon: Clock },
        { path: '/dock-analytics', label: 'Analytics', icon: BarChart3 },
      ]
    },

    // Quality & Procurement submenu
    {
      label: 'Quality & GRN',
      icon: ClipboardCheck,
      submenu: [
        { path: '/qc-inspection', label: 'QC Inspection', icon: ClipboardCheck },
        { path: '/grn-creation', label: 'GRN Creation', icon: FileText },
        { path: '/grn-management', label: 'GRN Management', icon: List },
        { path: '/mb52-stock', label: 'Stock Overview (MB52)', icon: Package },
      ]
    },

    // System submenu
    {
      label: 'System',
      icon: Database,
      submenu: [
        { path: '/sap-integration', label: 'SAP Integration', icon: Database },
        { path: '/management', label: 'Management', icon: Settings },
        { path: '/access-control', label: 'Access Control', icon: Shield },
        { path: '/system-settings', label: 'System Settings', icon: Settings },
      ]
    },
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header with Nexgensis Branding */}
      <header className="bg-white border-b border-gray-200 shadow-soft z-10">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-secondary">SCM</h1>
            <span className="text-sm text-gray-600 border-l border-gray-300 pl-4">Supply Chain Management</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Powered by</span>
            <a
              href="https://nexgensis.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg font-semibold text-primary hover:text-primary-dark transition-colors"
            >
              Nexgensis.com
            </a>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden bg-[#f0f2f5]">
        {/* Sidebar */}
        <aside className="w-64 bg-secondary text-white flex flex-col shadow-soft" style={{ WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>
          <nav className="flex-1 overflow-y-auto p-4 space-y-2" style={{ opacity: 1 }}>
            {navItems.map((item) => {
              const menuKey = getMenuKey(item.label);
              return item.submenu ? (
                // Parent menu with submenu
                <div key={item.label}>
                  <button
                    onClick={() => toggleMenu(menuKey)}
                    className="flex items-center justify-between w-full px-4 py-3 hover:bg-primary/20 rounded-lg transition-colors font-medium"
                    style={{ color: '#ffffff' }}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </div>
                    {expandedMenus[menuKey] ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  {expandedMenus[menuKey] && (
                    <div className="ml-8 mt-2 space-y-1">
                      {item.submenu.map((subItem) => (
                        <NavLink
                          key={subItem.path}
                          to={subItem.path}
                          className={({ isActive }) =>
                            `flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-primary font-semibold'
                                : 'hover:bg-primary/20'
                            }`
                          }
                          style={{ color: '#ffffff' }}
                        >
                          <subItem.icon className="w-4 h-4" />
                          <span className="text-sm">{subItem.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Regular menu item
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary font-semibold'
                        : 'hover:bg-primary/20 font-medium'
                    }`
                  }
                  style={{ color: '#ffffff' }}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="p-4 border-t border-secondary-light mt-auto">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 hover:bg-red-500/20 rounded-lg transition-colors w-full"
              style={{ color: '#ffffff' }}
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto flex flex-col">
          <div className="flex-1 p-6">
            <Outlet />
          </div>

          {/* Fixed Footer */}
          <footer className="bg-white border-t border-gray-200 shadow-soft mt-auto">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>&copy; {new Date().getFullYear()}</span>
                  <a
                    href="https://nexgensis.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary hover:text-primary-dark transition-colors"
                  >
                    Nexgensis.com
                  </a>
                  <span>|</span>
                  <span>All rights reserved</span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>SCM v1.0</span>
                  <span className="border-l pl-4">
                    Powered by <span className="font-semibold text-primary">Nexgensis</span>
                  </span>
                </div>
              </div>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default Layout;
